import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  ChartBar, UploadSimple, ClockCounterClockwise, UsersThree, IdentificationCard,
  Table, Gear, SignOut, List, Storefront, X, ShieldWarning,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { hasPerm } from "../lib/format";

const NAV = [
  { to: "/", label: "Dashboard", icon: ChartBar, perm: null, end: true },
  { to: "/upload", label: "Upload Sales", icon: UploadSimple, perm: "upload_data" },
  { to: "/history", label: "Upload History", icon: ClockCounterClockwise, perm: "view_upload_history" },
  { to: "/users", label: "Users", icon: UsersThree, perm: "manage_users" },
  { to: "/staff", label: "Staff", icon: IdentificationCard, perm: "manage_staff" },
  { to: "/templates", label: "Templates", icon: Table, perm: "manage_templates" },
  { to: "/settings", label: "Settings", icon: Gear, perm: null },
  { to: "/data-management", label: "Data & System", icon: ShieldWarning, perm: null, role: "admin" },
];

function NavItems({ user, onNavigate }) {
  const items = NAV.filter((n) => (!n.perm || hasPerm(user, n.perm)) && (!n.role || user?.role === n.role));
  return (
    <nav className="flex flex-col gap-1 px-3">
      {items.map((n) => {
        const Icon = n.icon;
        return (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={onNavigate}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-blue-700 text-white"
                  : "text-slate-300 hover:bg-slate-800 hover:text-white"
              }`
            }
          >
            <Icon size={20} weight="bold" />
            {n.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

function SidebarInner({ user, onLogout, onNavigate }) {
  return (
    <div className="flex h-full flex-col bg-slate-900 text-white">
      <div className="flex items-center gap-2.5 px-6 py-6">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-blue-700">
          <Storefront size={22} weight="bold" />
        </div>
        <div className="leading-tight">
          <div className="font-display text-base font-extrabold tracking-tight">S.S GROUP</div>
          <div className="text-[11px] text-slate-400">Sales Analytics</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        <NavItems user={user} onNavigate={onNavigate} />
      </div>
      <div className="border-t border-slate-800 p-3">
        <div className="mb-2 px-3">
          <div className="text-sm font-semibold">{user?.name}</div>
          <div className="text-xs capitalize text-slate-400">{user?.role} · @{user?.username}</div>
        </div>
        <button
          onClick={onLogout}
          data-testid="logout-btn"
          className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
        >
          <SignOut size={20} weight="bold" /> Sign out
        </button>
      </div>
    </div>
  );
}

export default function Layout({ children, title }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 lg:block">
        <SidebarInner user={user} onLogout={doLogout} />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[80%]">
            <SidebarInner user={user} onLogout={doLogout} onNavigate={() => setOpen(false)} />
            <button
              onClick={() => setOpen(false)}
              className="absolute -right-11 top-4 rounded-md bg-white/10 p-2 text-white"
              data-testid="close-drawer-btn"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur-md lg:px-8">
          <button
            className="lg:hidden"
            onClick={() => setOpen(true)}
            data-testid="open-drawer-btn"
          >
            <List size={24} weight="bold" />
          </button>
          <h1 className="font-display text-lg font-extrabold tracking-tight text-slate-900 lg:text-xl">
            {title}
          </h1>
          <div className="ml-auto flex items-center gap-2 lg:hidden">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-700 text-xs font-bold text-white">
              {user?.name?.[0]?.toUpperCase()}
            </div>
          </div>
        </header>

        <main className="p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
